import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, TextField, Typography, Alert, CircularProgress } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import { validatePasswordPolicy } from '../utils/passwordPolicy';

const PasswortResetPage: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ token?: string }>();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => {
    const queryToken = searchParams.get('token');
    if (queryToken) return queryToken;
    return params.token || '';
  }, [searchParams, params.token]);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const passwordPolicy = useMemo(() => validatePasswordPolicy(newPassword), [newPassword]);

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
    }
  }, [token, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    if (!passwordPolicy.valid) {
      setError(`Passwort-Richtlinie verletzt: ${passwordPolicy.errors.join('; ')}`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    setLoading(true);
    try {
      await authService.confirmPasswordReset(token, newPassword);
      navigate('/login?reset=success', { replace: true });
    } catch (err: any) {
      navigate('/login', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#181c34',
        background: 'radial-gradient(ellipse at 50% 0%, #1e2340 0%, #181c34 70%)',
      }}
    >
      <Card
        sx={{
          width: { xs: '100%', sm: 460 },
          maxWidth: '100%',
          mx: { xs: 2, sm: 'auto' },
          bgcolor: '#212744',
          border: '1px solid #2e3560',
          borderRadius: 3,
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography variant="h5" sx={{ color: '#3dd47e', fontWeight: 700, mb: 1 }}>
            Passwort zurücksetzen
          </Typography>
          <Typography variant="body2" sx={{ color: '#8899aa', mb: 3 }}>
            Vergib ein neues Passwort für dein STARK-AIO Konto.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <TextField
              label="Neues Passwort"
              type="password"
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              sx={{ mb: 2 }}
              helperText="20+ Zeichen, Gross/Klein, Ziffer, Sonderzeichen, keine simplen Muster"
            />
            {newPassword && (
              <Box sx={{ border: '1px solid #2e3560', borderRadius: 1.5, p: 1.5, mb: 2 }}>
                {passwordPolicy.checks.map((check) => (
                  <Typography
                    key={check.key}
                    variant="caption"
                    sx={{ display: 'block', color: check.passed ? '#3dd47e' : '#ef5350' }}
                  >
                    {check.passed ? 'OK ' : 'X '} {check.label}
                  </Typography>
                ))}
              </Box>
            )}
            <TextField
              label="Passwort bestätigen"
              type="password"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading || !token}
              sx={{
                py: 1.2,
                bgcolor: '#3dd47e',
                color: '#181c34',
                fontWeight: 700,
                '&:hover': { bgcolor: '#2ba862' },
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: '#181c34' }} /> : 'Passwort speichern'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PasswortResetPage;
